// src/features/hector-agent-casediscovery-v3/services/MessageHandler.ts

import { BackendMessage } from '../types';

/**
 * Extract payload type from a BackendMessage by its type
 */
type MessageFor<T extends BackendMessage['type']> = Extract<
  BackendMessage,
  { type: T }
>;

type ExtractPayload<T extends BackendMessage['type']> = MessageFor<T> extends {
  payload: infer Payload;
} ? Payload : MessageFor<T>;

/**
 * Handler function type for a specific message type
 */
type MessageHandlerFn<T extends BackendMessage['type']> = (
  payload: ExtractPayload<T>,
  message: Extract<BackendMessage, { type: T }>
) => void;

type StoredMessageHandler = (payload: unknown, message: BackendMessage) => void;

/**
 * MessageHandler - Gestionnaire de messages WebSocket V3
 *
 * Pattern générique permettant d'enregistrer des handlers
 * pour n'importe quel type de message backend.
 *
 * @example
 * const handler = new MessageHandler();
 * handler.on('agent_thinking', (payload) => console.log(payload.delta));
 * handler.on('agent_done', (payload) => console.log(payload.reason));
 */
export class MessageHandler {
  private handlers = new Map<string, Set<StoredMessageHandler>>();

  /**
   * Register a handler for a specific message type
   */
  on<T extends BackendMessage['type']>(
    type: T,
    handler: MessageHandlerFn<T>
  ): void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler as unknown as StoredMessageHandler);
  }

  /**
   * Unregister a specific handler
   */
  off<T extends BackendMessage['type']>(
    type: T,
    handler: MessageHandlerFn<T>
  ): void {
    this.handlers.get(type)?.delete(handler as unknown as StoredMessageHandler);
  }

  /**
   * Dispatch a message to all registered handlers
   */
  handle(message: BackendMessage): void {
    const typeHandlers = this.handlers.get(message.type);
    if (typeHandlers) {
      // skill_change_* uses the shared exact top-level frame contract, whereas
      // legacy CD frames nest their domain data under payload.
      const payload = 'payload' in message ? message.payload : message;
      typeHandlers.forEach((handler) => handler(payload, message));
    }
  }

  /**
   * Remove all handlers
   */
  clear(): void {
    this.handlers.clear();
  }
}
